import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import Dashboard from './Dashboard';
import EmployeeManagement from './EmployeeManagement';
import AttendanceManagement from './AttendanceManagement/AttendanceManagement';
import DateWiseAttendance from './AttendanceManagement/DateWise/DateWiseAttendance';
import DateSpecificAttendance from './AttendanceManagement/DateWise/DateSpecificAttendance';
import EmployeeWiseAttendance from './AttendanceManagement/EmployeeWise/EmployeeWiseAttendance';
import EmployeeAttendanceCalendar from './AttendanceManagement/EmployeeWise/EmployeeAttendanceCalendar';
import AttendanceOverview from './AttendanceManagement/Overview/AttendanceOverview';
import LeaveManagement from './LeaveManagement';
import TaskManagement from './TaskManagement';
import CreateTask from './CreateTask';
import TaskDetail from './TaskDetail';
import TaskAnalytics from './TaskAnalytics';
import Performance from './Performance';
//import SalaryReports from './SalaryReports';
import HolidayManagement from './HolidayManagement';
import Onboarding from './Onboarding';
import AttendanceModificationRequests from './AttendanceModificationRequests';
import PayrollManagement from './PayrollManagement';
import PayrollEmployeeDetail from './PayrollEmployeeDetail';
import OfferLetters from './OfferLetters';
import useAdminSidebarCounts from '../../hooks/useAdminSidebarCounts';
import WFHManagement from './WFHManagement';
import ProbationStatus from './ProbationStatus';
import InternshipStatus from './InternshipStatus';
import FullAndFinalSettlementManagement from './FullAndFinalSettlementManagement';
import PolicyManagement from './PolicyManagement';

import BirthdayManagement from '../admin/BirthdayManagement';
import BirthdayPopup from '../common/BirthdayPopup';
import BirthdayBell from '../common/BirthdayBell';


const Rating = () => (
  <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center shadow-sm">
    <h2 className="text-2xl font-bold text-gray-900">Rating</h2>
    <p className="mt-2 text-sm text-gray-500">This section is ready. Content can be added later.</p>
  </div>
);

const AdminDashboard = () => {
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { counts, refreshAllCounts } = useAdminSidebarCounts();

  return (
    <div className="flex h-screen bg-gray-100">
      <BirthdayPopup />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        counts={counts}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="ml-2 text-xl font-semibold text-gray-900">
                Admin Dashboard
              </h1>
              <span className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                Live System
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <BirthdayBell />
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-semibold text-gray-900">Admin</span>
              </div>
              <div className="h-8 w-px bg-gray-200 hidden sm:block" />
              <button
                onClick={logout}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <div className="container mx-auto px-6 md:px-4 py-8">
            <Routes>
              <Route path="/" element={<Navigate to="/admin/dashboard" />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/employees" element={<EmployeeManagement />} />
              <Route
                path="/probation"
                element={<ProbationStatus onSidebarCountsRefresh={refreshAllCounts} />}
              />
              <Route
                path="/internship"
                element={<InternshipStatus onSidebarCountsRefresh={refreshAllCounts} />}
              />
              <Route
                path="/onboarding"
                element={<Onboarding onSidebarCountsRefresh={refreshAllCounts} />}
              />
              <Route path="/attendance" element={<AttendanceManagement />} />
              <Route path="/attendance/date-wise" element={<DateWiseAttendance />} />
              <Route path="/attendance/date-wise/:date" element={<DateSpecificAttendance />} />
              <Route path="/attendance/employee-wise" element={<EmployeeWiseAttendance />} />
              <Route path="/attendance/employee-wise/:employeeId" element={<EmployeeAttendanceCalendar />} />
              <Route path="/attendance/overview" element={<AttendanceOverview />} />
              <Route
                path="/leaves"
                element={<LeaveManagement onSidebarCountsRefresh={refreshAllCounts} />}
              />
              <Route
                path="/comp-off"
                element={<LeaveManagement compOffOnly onSidebarCountsRefresh={refreshAllCounts} />}
              />
              <Route
                path="/tasks"
                element={<TaskManagement onSidebarCountsRefresh={refreshAllCounts} />}
              />
              <Route path="/tasks/create" element={<CreateTask />} />
              <Route
                path="/tasks/:id"
                element={<TaskDetail onSidebarCountsRefresh={refreshAllCounts} />}
              />
              <Route path="/tasks/analytics" element={<TaskAnalytics />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/rating" element={<Rating />} />
              {/* <Route path="/salary-reports" element={<SalaryReports />} /> */}
              <Route path="/payroll" element={<PayrollManagement />} />
              <Route path="/payroll/:employeeId" element={<PayrollEmployeeDetail />} />
              <Route path="/offer-letters/*" element={<OfferLetters />} />
              <Route path="/holidays" element={<HolidayManagement />} />
              <Route path="/birthdays" element={<BirthdayManagement />} />
              <Route path="/policies" element={<PolicyManagement />} />
              <Route path="/attendance-requests" element={<AttendanceModificationRequests />} />
              <Route
                path="/wfh"
                element={<WFHManagement onSidebarCountsRefresh={refreshAllCounts} />}
              />
              <Route
                path="/full-and-final"
                element={<FullAndFinalSettlementManagement onSidebarCountsRefresh={refreshAllCounts} />}
              />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
